import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Star, Plus, Trash2, TrendingUp, TrendingDown, Edit2, Eye, X, Check
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const ICONS = {
  cocoa: "🍫", coffee: "☕", sugar: "🍬", cotton: "🧵", wheat: "🌾", 
  maize: "🌽", soybean: "🫘", palm_oil: "🌴", gold: "🥇", crude_oil: "🛢️",
  copper: "🔶", natural_gas: "🔥", silver: "🥈", zinc: "⚪"
};

export default function WatchlistsPage() {
  const [watchlists, setWatchlists] = useState([]);
  const [selectedWatchlist, setSelectedWatchlist] = useState(null);
  const [commodities, setCommodities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [addCommodityOpen, setAddCommodityOpen] = useState(false);
  const [editingName, setEditingName] = useState(null);
  
  const [newWatchlist, setNewWatchlist] = useState({
    name: "",
    description: "",
    commodities: []
  });
  
  const [selectedCommodityToAdd, setSelectedCommodityToAdd] = useState("");

  const fetchWatchlists = useCallback(async () => {
    try {
      const [watchlistRes, commoditiesRes] = await Promise.all([
        api.get("/watchlists"),
        api.get("/price-alerts/commodities")
      ]);
      setWatchlists(watchlistRes.data.watchlists || []);
      setCommodities(commoditiesRes.data.commodities || []);
      
      // Select first watchlist if none selected
      if (!selectedWatchlist && watchlistRes.data.watchlists?.length > 0) {
        setSelectedWatchlist(watchlistRes.data.watchlists[0]);
      }
    } catch (err) {
      console.error("Error fetching watchlists:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedWatchlist]);

  useEffect(() => {
    fetchWatchlists();
  }, [fetchWatchlists]);

  const handleCreateWatchlist = async () => {
    try {
      const { data } = await api.post("/watchlists", {
        name: newWatchlist.name,
        description: newWatchlist.description,
        commodities: newWatchlist.commodities
      });
      
      toast.success("Watchlist created!");
      setDialogOpen(false);
      setNewWatchlist({ name: "", description: "", commodities: [] });
      fetchWatchlists();
      setSelectedWatchlist(data.watchlist);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create watchlist");
    }
  };

  const handleDeleteWatchlist = async (watchlistId) => {
    try {
      await api.delete(`/watchlists/${watchlistId}`);
      toast.success("Watchlist deleted");
      if (selectedWatchlist?.id === watchlistId) {
        setSelectedWatchlist(null);
      }
      fetchWatchlists();
    } catch (err) {
      toast.error("Failed to delete watchlist");
    }
  };

  const handleAddCommodity = async () => {
    if (!selectedWatchlist || !selectedCommodityToAdd) return;
    
    try {
      await api.post(`/watchlists/${selectedWatchlist.id}/commodities`, {
        commodity_id: selectedCommodityToAdd
      });
      
      toast.success("Commodity added!");
      setAddCommodityOpen(false);
      setSelectedCommodityToAdd("");
      fetchWatchlists();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add commodity");
    }
  };

  const handleRemoveCommodity = async (commodityId) => {
    if (!selectedWatchlist) return;
    
    try {
      await api.delete(`/watchlists/${selectedWatchlist.id}/commodities/${commodityId}`);
      toast.success("Commodity removed");
      fetchWatchlists();
    } catch (err) {
      toast.error("Failed to remove commodity");
    }
  };

  const handleUpdateName = async () => {
    if (!editingName) return;
    
    try {
      await api.put(`/watchlists/${editingName.id}`, {
        name: editingName.name
      });
      toast.success("Watchlist updated");
      setEditingName(null);
      fetchWatchlists();
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  const toggleCommodityInNew = (commodityId) => {
    setNewWatchlist(prev => ({
      ...prev,
      commodities: prev.commodities.includes(commodityId)
        ? prev.commodities.filter(c => c !== commodityId)
        : [...prev.commodities, commodityId]
    }));
  };

  // Get current watchlist data with prices
  const currentWatchlistData = watchlists.find(w => w.id === selectedWatchlist?.id);
  const availableCommodities = commodities.filter(
    c => !currentWatchlistData?.commodities?.includes(c.id)
  );

  return (
    <div data-testid="watchlists-page" className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wider text-zinc-100">
            Watchlists
          </h1>
          <p className="text-xs text-zinc-500 font-mono mt-1">
            Organize and track your favorite commodities
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-[#00ff41] text-black hover:bg-[#00ff41]/80 font-mono text-xs gap-2">
              <Plus className="w-4 h-4" /> New Watchlist
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 rounded-none max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg uppercase tracking-wider text-zinc-100">
                Create Watchlist
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <label className="text-[10px] text-zinc-500 uppercase font-mono mb-2 block">Name</label>
                <Input
                  placeholder="e.g., Soft Commodities"
                  value={newWatchlist.name}
                  onChange={(e) => setNewWatchlist(p => ({ ...p, name: e.target.value }))}
                  className="rounded-none border-zinc-700 bg-zinc-950 text-sm font-mono"
                />
              </div>
              
              <div>
                <label className="text-[10px] text-zinc-500 uppercase font-mono mb-2 block">Description</label>
                <Input
                  placeholder="Optional description..."
                  value={newWatchlist.description}
                  onChange={(e) => setNewWatchlist(p => ({ ...p, description: e.target.value }))}
                  className="rounded-none border-zinc-700 bg-zinc-950 text-sm font-mono"
                />
              </div>
              
              <div>
                <label className="text-[10px] text-zinc-500 uppercase font-mono mb-2 block">Select Commodities</label>
                <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto">
                  {commodities.map(c => (
                    <button
                      key={c.id}
                      onClick={() => toggleCommodityInNew(c.id)}
                      className={`flex items-center gap-2 p-2 rounded-none border transition-colors ${
                        newWatchlist.commodities.includes(c.id)
                          ? 'border-[#00ff41] bg-[#00ff41]/10'
                          : 'border-zinc-700 bg-zinc-950 hover:border-zinc-600'
                      }`}
                    >
                      <span className="text-lg">{ICONS[c.id] || "📊"}</span>
                      <span className="font-mono text-xs text-zinc-300">{c.name}</span>
                      {newWatchlist.commodities.includes(c.id) && (
                        <Check className="w-3 h-3 text-[#00ff41] ml-auto" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none border-zinc-700 font-mono text-xs">
                Cancel
              </Button>
              <Button onClick={handleCreateWatchlist} className="rounded-none bg-[#00ff41] text-black hover:bg-[#00ff41]/80 font-mono text-xs">
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        {/* Watchlists List */}
        <div className="md:col-span-1">
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400">
                Your Watchlists
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              {loading ? (
                <div className="p-4 text-center">
                  <div className="animate-spin w-5 h-5 border-2 border-[#00ff41] border-t-transparent rounded-full mx-auto" />
                </div>
              ) : watchlists.length === 0 ? (
                <div className="p-4 text-center">
                  <Star className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                  <p className="text-zinc-500 font-mono text-xs">No watchlists yet</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {watchlists.map(wl => (
                    <div
                      key={wl.id}
                      className={`group flex items-center justify-between p-2 rounded-none cursor-pointer transition-colors ${
                        selectedWatchlist?.id === wl.id
                          ? 'bg-[#00ff41]/10 border border-[#00ff41]/30'
                          : 'hover:bg-zinc-900 border border-transparent'
                      }`}
                      onClick={() => setSelectedWatchlist(wl)}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Star className={`w-4 h-4 flex-shrink-0 ${selectedWatchlist?.id === wl.id ? 'text-[#00ff41]' : 'text-zinc-500'}`} />
                        {editingName?.id === wl.id ? (
                          <Input
                            value={editingName.name}
                            onChange={(e) => setEditingName(p => ({ ...p, name: e.target.value }))}
                            onBlur={handleUpdateName}
                            onKeyDown={(e) => e.key === 'Enter' && handleUpdateName()}
                            className="h-6 text-xs rounded-none border-zinc-700 bg-zinc-950"
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <span className="font-mono text-xs text-zinc-200 truncate">{wl.name}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Badge variant="outline" className="text-[10px] rounded-none border-zinc-700">
                          {wl.commodities?.length || 0}
                        </Badge>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingName({ id: wl.id, name: wl.name });
                          }}
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 text-[#ff3b30]"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteWatchlist(wl.id);
                          }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Watchlist Detail */}
        <div className="md:col-span-3">
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                  <Eye className="w-4 h-4 text-[#00ff41]" />
                  {currentWatchlistData?.name || "Select a Watchlist"}
                </CardTitle>
                
                {selectedWatchlist && (
                  <Dialog open={addCommodityOpen} onOpenChange={setAddCommodityOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="rounded-none bg-zinc-800 hover:bg-zinc-700 font-mono text-xs gap-1">
                        <Plus className="w-3 h-3" /> Add
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-zinc-900 border-zinc-800 rounded-none max-w-sm">
                      <DialogHeader>
                        <DialogTitle className="font-heading text-lg uppercase tracking-wider text-zinc-100">
                          Add Commodity
                        </DialogTitle>
                      </DialogHeader>
                      
                      <div className="py-4">
                        <Select value={selectedCommodityToAdd} onValueChange={setSelectedCommodityToAdd}>
                          <SelectTrigger className="rounded-none border-zinc-700 bg-zinc-950">
                            <SelectValue placeholder="Select commodity" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-700 rounded-none">
                            {availableCommodities.map(c => (
                              <SelectItem key={c.id} value={c.id} className="font-mono">
                                {ICONS[c.id] || "📊"} {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setAddCommodityOpen(false)} className="rounded-none border-zinc-700 font-mono text-xs">
                          Cancel
                        </Button>
                        <Button onClick={handleAddCommodity} className="rounded-none bg-[#00ff41] text-black hover:bg-[#00ff41]/80 font-mono text-xs">
                          Add
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
              {currentWatchlistData?.description && (
                <p className="text-xs text-zinc-500 mt-1">{currentWatchlistData.description}</p>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {!selectedWatchlist ? (
                <div className="p-8 text-center">
                  <Star className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500 font-mono text-sm">Select or create a watchlist</p>
                </div>
              ) : (currentWatchlistData?.commodities_data || []).length === 0 ? (
                <div className="p-8 text-center">
                  <Star className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500 font-mono text-sm">No commodities in this watchlist</p>
                  <p className="text-zinc-600 text-xs mt-1">Click "Add" to add commodities</p>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <div className="divide-y divide-zinc-800">
                    {(currentWatchlistData?.commodities_data || []).map(c => (
                      <div key={c.id} className="p-4 hover:bg-zinc-900/50 transition-colors flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{ICONS[c.id] || "📊"}</span>
                          <div>
                            <p className="font-mono font-medium text-zinc-100">{c.name}</p>
                            <p className="text-xs text-zinc-500 font-mono">{c.unit}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="font-mono font-bold text-zinc-100">${c.price.toLocaleString()}</p>
                            <div className={`flex items-center justify-end gap-1 text-xs font-mono ${c.change >= 0 ? 'text-[#00ff41]' : 'text-[#ff3b30]'}`}>
                              {c.change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                              {c.change >= 0 ? '+' : ''}{c.change.toFixed(2)}%
                            </div>
                          </div>
                          
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-zinc-500 hover:text-[#ff3b30]"
                            onClick={() => handleRemoveCommodity(c.id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
